import { useCallback, useEffect, useRef, useState } from "react";
import {
  claimCommentDictation,
  commentDictationClaimedBy,
  releaseCommentDictationClaim,
  type CommentDictationTarget,
} from "./comment-dictation-target";
import { isDictationSettled } from "./dictation-status";
import { useDictation } from "./use-dictation";

/** Mic-button dictation into one composer's `target`. A click on a settled
 *  status claims the recording it starts; a click while that recording runs
 *  stops it; a recording owned by Denker or another composer is left alone.
 *  The claim is released once the status is idle again, on `release()`, or on
 *  unmount — which also cancels a still-running owned recording, so its
 *  transcript never lands somewhere the user did not choose. The transcript
 *  is delivered to the handlers of the latest render, not the click's.
 *  `ownsRecording` lets a host keep the composer mounted meanwhile. */
export function useCommentDictationClaim(target: CommentDictationTarget) {
  const status = useDictation((state) => state.status);
  const targetRef = useRef(target);
  targetRef.current = target;
  // The id actually claimed, so a release always targets what was claimed.
  const claimedIdRef = useRef<string | null>(null);
  const [claimedId, setClaimedId] = useState<string | null>(null);

  // A recording another start took over (e.g. the shortcut after an error)
  // is no longer this composer's, even before the idle release runs.
  const ownsClaim = useCallback(() => {
    const claimedId = claimedIdRef.current;
    return claimedId !== null && commentDictationClaimedBy(claimedId);
  }, []);

  const release = useCallback(() => {
    const claimedId = claimedIdRef.current;
    if (claimedId === null) return;
    claimedIdRef.current = null;
    setClaimedId(null);
    releaseCommentDictationClaim(claimedId);
  }, []);

  useEffect(() => {
    if (status === "idle") release();
  }, [release, status]);

  useEffect(
    () => () => {
      if (ownsClaim()) {
        const dictation = useDictation.getState();
        if (!isDictationSettled(dictation.status)) void dictation.cancel();
      }
      release();
    },
    [ownsClaim, release],
  );

  /** Returns the toggle in flight, or null when the click was ignored. */
  const toggle = useCallback(
    (): Promise<void> | null => {
      const dictation = useDictation.getState();
      if (isDictationSettled(dictation.status)) {
        const { id, disableScreenshotCapture } = targetRef.current;
        claimedIdRef.current = id;
        setClaimedId(id);
        claimCommentDictation({
          id,
          disableScreenshotCapture,
          deliverTranscript: (text, images) =>
            targetRef.current.deliverTranscript(text, images),
        });
      } else if (!ownsClaim()) {
        return null;
      }
      return dictation.toggle().finally(() => {
        // Never started (or already done) with no status change to observe.
        if (useDictation.getState().status === "idle") release();
      });
    },
    [ownsClaim, release],
  );

  return {
    toggle,
    release,
    /** This composer's claim is still live (recording, processing, or its
     *  error); re-read on every dictation status change. */
    ownsRecording:
      claimedId !== null && commentDictationClaimedBy(claimedId),
  };
}
